import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useAppContext } from 'src/contexts/AppContext';
import {
  Content,
  getContent,
  useContent,
  useContentContext,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Radio, RadioGroup, Stack, TooltipPopover } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { BoostStrategyIcon } from 'src/svgs/BoostStrategyIcon';
import { EditIcon, InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FLIGHT_DURATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { posChangedField, posFieldFromValues } from 'src/utils/posFieldUtils';
import {
  BiddingMode,
  Event,
  Feature,
  FlightDuration,
  ListingMetrics,
  SpendStrategy,
  SponsoredListingsCampaign,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

import {
  DEFAULT_BASE_BID,
  getProspectiveBudget,
  MINIMUM_BASE_BID,
} from '../sponsoredListings.util';
import * as styles from './CampaignModal.css';
import { CampaignModalFooter } from './CampaignModalFooter';
import { ZoneTargetingMapEmbedded } from './ZoneTargetingMapEmbedded';

const dailyOnlyFlightDuration = {
  [FlightDuration.Daily]: FLIGHT_DURATION_TO_CID[FlightDuration.Daily],
} as Record<FlightDuration, string>;

export const CampaignModalBodyV2 = ({
  campaign,
  event,
  disabled,
  eventNameDisplay,
  metrics,
  hideZoneList,
  isMapVisible,
  onToggleMapVisible,
}: {
  campaign?: SponsoredListingsCampaign | null;
  event: Event;
  disabled: boolean;
  eventNameDisplay: string;
  metrics?: ListingMetrics;
  hideZoneList?: boolean;
  isMapVisible?: boolean;
  onToggleMapVisible?: Dispatch<SetStateAction<boolean>>;
}) => {
  const { isDarkMode } = useSiteTheme();
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const { ticketClasses, isMapLoading } = useEventMapContext();
  const [hasManualNameChanged, setHasManualNameChanged] = useState(false);
  const [hasManualBudgetChanged, setHasManualBudgetChanged] = useState(false);
  const hasSpecificZonesFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_SpecificZonesTargeting
  );
  const hasFirst3RowsOnlyFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_First3RowsOnly
  );
  const hasBoostStrategyFeature = useUserHasFeature(Feature.BoostStrategy);
  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext]
  );

  const contentContext = useContentContext();

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<SponsoredListingsCampaignInput>();

  const campaignNamePlaceholder = useContent(ContentId.EnterAdCampaignName);

  const campaignId = watch('campaignId');
  const campaignName = watch('campaignName');
  const flightDuration = watch('flightDuration');
  const biddingMode = watch('biddingMode');
  const baseBid = watch('baseBid');
  const recurringFlightBudget = watch('recurringFlightBudget');
  const currentFlightBudget = watch('currentFlightBudget');
  const targetTicketClassIds = watch('targetTicketClassIds');
  const firstThreeRowsOnly = watch('firstThreeRowsOnly');
  const spendStrategy = watch('spendStrategy');

  const handleTargetChange = useCallback(
    (value: string) => {
      if (value === 'default') {
        setValue('targetTicketClassIds', posChangedField(null));
      } else {
        const currentIds = targetTicketClassIds?.value ?? [];
        setValue(
          'targetTicketClassIds',
          posChangedField(currentIds.length > 0 ? currentIds : [])
        );
        // When the user freshly switches to Specific Zones, always reveal the map
        onToggleMapVisible?.(true);
      }
    },
    [setValue, targetTicketClassIds, onToggleMapVisible]
  );

  const untilLabel = useContent(ContentId.Until);

  const campaignEndDateUtc = useMemo(
    () =>
      campaign?.activeFlight?.dateRange?.end
        ? toZonedTime(parseISO(campaign.activeFlight.dateRange.end!), 'UTC')
        : null,
    [campaign?.activeFlight?.dateRange?.end]
  );

  const requiredText = useContent(ContentId.Required);
  const currentFlightBudgetLessThanSpendText = useContent(
    ContentId.CurrentFlightBudgetLessThanSpendError
  );
  const flightDurationText = useContent(
    FLIGHT_DURATION_TO_CID[flightDuration.value ?? FlightDuration.Daily]
  );
  const baseBidExceedsBudgetText = useFormattedContent(
    FormatContentId.BaseBidExceedsBudgetError,
    [`${flightDurationText}`]
  );
  const budgetMustBeGreaterThanBaseBidText = useContent(
    ContentId.BudgetMustBeGreaterThanBaseBidError
  );

  return (
    <Stack
      direction="column"
      gap="l"
      width="full"
      className={styles.boostBodyContainer}
    >
      <Stack direction="column" gap="m" width="full">
        <Stack gap="l" width="full">
          <Stack
            alignItems="center"
            width="fit"
            className={styles.itemLabel}
          >
            <span className={styles.sectionStepNumber}>1.</span>
            <Content id={ContentId.CampaignName} />
          </Stack>
          <Stack width="full">
            <PosFormField errors={errors.campaignName?.message}>
              <PosTextField
                disabled={disabled}
                errorState={getTextFieldState(errors.campaignName)}
                value={campaignName?.value ?? ''}
                placeholder={campaignNamePlaceholder}
                type="text"
                inputMode="text"
                onChange={(e) => {
                  setHasManualNameChanged(true);
                  setValue(
                    'campaignName',
                    posFieldFromValues(e.target.value, campaign?.campaignName)
                  );
                  clearErrors('campaignName');
                }}
                onBlur={() => {
                  if (
                    !campaignName.value ||
                    campaignName.value.trim().length === 0
                  ) {
                    setError('campaignName', {
                      type: 'required',
                      message: requiredText,
                    });
                  }
                }}
              />
            </PosFormField>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="column" gap="m" width="full">
        <Stack alignItems="center" className={styles.itemLabel}>
          <span className={styles.sectionStepNumber}>2.</span>
          <Content id={ContentId.Bidding} />
        </Stack>
        <RadioGroup
          value={biddingMode.value}
          disabled={disabled}
          onValueChange={(v: BiddingMode) => {
            setValue(
              'biddingMode',
              posFieldFromValues(v, campaign?.biddingMode)
            );

            if (v === BiddingMode.Manual) {
              if (baseBid?.value == null) {
                setValue(
                  'baseBid',
                  posFieldFromValues(DEFAULT_BASE_BID, campaign?.baseBid)
                );
              }
              setValue(
                'spendStrategy',
                posFieldFromValues(null, campaign?.spendStrategy ?? null)
              );
            }
          }}
        >
          <Radio
            disabled={disabled}
            value={BiddingMode.Automatic}
            variant="outline"
            label={<Content id={ContentId.Automatic} />}
            labelSubtext={
              <Content id={ContentId.Automatic_Bidding_Description} />
            }
          />
          <Radio
            disabled={disabled}
            value={BiddingMode.Manual}
            variant="outline"
            label={<Content id={ContentId.Manual} />}
            labelSubtext={<Content id={ContentId.Manual_Bidding_Description} />}
          />
        </RadioGroup>
      </Stack>
      <Stack
        direction="column"
        gap="m"
        width="full"
        className={styles.subsectionContainer}
      >
        {biddingMode.value === BiddingMode.Manual && (
          <Stack gap="l" width="full">
            <Stack gap="l" width="half">
              <Stack
                gap="m"
                alignItems="center"
                width="full"
                className={styles.itemLabel}
              >
                <span>
                  <Content id={ContentId.SetBidAmount} />
                </span>
                <TooltipPopover
                  contentVariant={isDarkMode ? 'light' : 'dark'}
                  triggerContent={
                    <InfoOutlineIcon
                      withHoverEffect
                      fill={vars.color.iconPrimary}
                      size={vars.iconSize.s}
                    />
                  }
                >
                  <Content id={ContentId.SetBidAmountDescription} />
                </TooltipPopover>
              </Stack>
            </Stack>
            <Stack width="half">
              <PosFormField errors={errors.baseBid?.message}>
                <PosCurrencyField
                  disabled={disabled}
                  errorState={getTextFieldState(errors.baseBid)}
                  value={baseBid?.value ?? ''}
                  onChange={(e) => {
                    let newNumber = e.target.value
                      ? Number(e.target.value)
                      : null;

                    if (typeof newNumber !== 'number' || isNaN(newNumber)) {
                      newNumber = null;
                    }
                    setHasManualBudgetChanged(true);
                    setValue(
                      'baseBid',
                      posFieldFromValues(newNumber, campaign?.baseBid)
                    );
                    clearErrors('baseBid');
                  }}
                  uiCurrency={uiCurrency}
                  onBlur={() => {
                    // Validate if manual bidding is selected
                    if (biddingMode.value === BiddingMode.Manual) {
                      if (baseBid.value == null) {
                        setError('baseBid', {
                          type: 'required',
                          message: requiredText,
                        });
                      } else if (baseBid.value < MINIMUM_BASE_BID) {
                        setValue(
                          'baseBid',
                          posFieldFromValues(
                            MINIMUM_BASE_BID,
                            campaign?.baseBid
                          )
                        );
                      }
                    }
                    // Validate that the base bid cannot be more than the recurring flight budget
                    if (
                      biddingMode.value === BiddingMode.Manual &&
                      baseBid.value &&
                      // Base bid cannot exceed either the recurring budget or current flight budget (if editing active flight)
                      (baseBid.value > (recurringFlightBudget.value ?? 0) ||
                        (currentFlightBudget.value &&
                          baseBid.value > currentFlightBudget.value))
                    ) {
                      setError('baseBid', {
                        type: 'max',
                        message: baseBidExceedsBudgetText as string,
                      });
                    } else {
                      clearErrors('recurringFlightBudget');
                      clearErrors('currentFlightBudget');
                    }
                  }}
                />
              </PosFormField>
            </Stack>
          </Stack>
        )}
        {campaign?.activeFlight && (
          <Stack gap="l" width="full">
            <Stack direction="column" width="half">
              <Stack className={styles.itemLabel} gap="m" alignItems="center">
                <span>
                  <Content id={ContentId.ActiveBudget} />{' '}
                  <span>
                    {campaignEndDateUtc &&
                      `(${format(campaignEndDateUtc, 'M/d')})`}
                  </span>
                </span>
                <TooltipPopover
                  contentVariant={isDarkMode ? 'light' : 'dark'}
                  triggerContent={
                    <InfoOutlineIcon
                      withHoverEffect
                      fill={vars.color.iconPrimary}
                      size={vars.iconSize.s}
                    />
                  }
                >
                  <span>
                    {campaignEndDateUtc &&
                      `(${untilLabel} ${format(campaignEndDateUtc, 'M/d/yyyy @ h:mm aa ')} UTC)`}
                  </span>
                </TooltipPopover>
              </Stack>
              <span className={styles.information}>
                <Content id={ContentId.AlreadySpent} />:{' '}
                {applyNumberFormatting({
                  inputNumber: campaign.activeFlight.flightSpend,
                  currencyCode: uiCurrency.code,
                  currencyDecimalDigits: uiCurrency.dec,
                })}
              </span>
            </Stack>
            <Stack width="half">
              <PosFormField errors={errors.currentFlightBudget?.message}>
                <PosCurrencyField
                  disabled={disabled}
                  errorState={getTextFieldState(errors.currentFlightBudget)}
                  value={currentFlightBudget?.value ?? ''}
                  onChange={(e) => {
                    let newNumber = e.target.value
                      ? Number(e.target.value)
                      : null;

                    if (typeof newNumber !== 'number' || isNaN(newNumber)) {
                      newNumber = null;
                    }
                    setHasManualBudgetChanged(true);
                    setValue(
                      'currentFlightBudget',
                      posFieldFromValues(
                        newNumber,
                        campaign.activeFlight?.flightBudget
                      )
                    );
                    clearErrors('currentFlightBudget');
                  }}
                  uiCurrency={uiCurrency}
                  onBlur={() => {
                    // Validate the current flight budget has to be greater than spend
                    if (
                      campaign?.activeFlight &&
                      (currentFlightBudget.value || 0) <
                        (campaign.activeFlight.flightSpend || 0)
                    ) {
                      setError('currentFlightBudget', {
                        type: 'min',
                        message: currentFlightBudgetLessThanSpendText,
                      });
                    }

                    // Validate that the base bid cannot be more than the recurring flight budget
                    if (
                      biddingMode.value === BiddingMode.Manual &&
                      baseBid.value &&
                      // Base bid cannot exceed either the recurring budget or current flight budget (if editing active flight)
                      (baseBid.value > (recurringFlightBudget.value ?? 0) ||
                        (currentFlightBudget.value &&
                          baseBid.value > currentFlightBudget.value))
                    ) {
                      setError('currentFlightBudget', {
                        type: 'max',
                        message: budgetMustBeGreaterThanBaseBidText as string,
                      });
                    } else {
                      clearErrors('baseBid');
                    }
                  }}
                />
              </PosFormField>
            </Stack>
          </Stack>
        )}
        <Stack gap="l" width="full">
          <Stack gap="l" width="half">
            <Stack
              gap="m"
              alignItems="center"
              width="full"
              className={styles.itemLabel}
            >
              <PosEnumSelect
                className={styles.itemLabel}
                fontWeight={400}
                disabled={disabled}
                variant="textPlain"
                value={flightDuration?.value ?? FlightDuration.Daily}
                valueOptionsContent={dailyOnlyFlightDuration}
                sortMode="none"
                displayText={`${campaignId > 0 ? getContent(ContentId.Future, contentContext) : ''} ${getContent(FLIGHT_DURATION_TO_CID[flightDuration?.value ?? FlightDuration.Daily], contentContext)}`}
                onChange={(option: FlightDuration | null) => {
                  if (option) {
                    setValue(
                      'flightDuration',
                      posFieldFromValues(option, campaign?.flightDuration)
                    );

                    // If new campaign, apply auto-generation of names and budget logic
                    if (!hasManualNameChanged) {
                      setValue(
                        'campaignName',
                        posFieldFromValues(
                          `${eventNameDisplay} - ${option}`,
                          campaign?.campaignName
                        )
                      );
                    }
                    if (!hasManualBudgetChanged) {
                      const newBudget = getProspectiveBudget(
                        metrics?.unsoldCst,
                        option,
                        event.datesUtc?.start ?? event.dates.start
                      );
                      if (newBudget != null) {
                        setValue(
                          'recurringFlightBudget',
                          posFieldFromValues(
                            newBudget,
                            campaign?.recurringFlightBudget
                          )
                        );
                      } else {
                        clearErrors('baseBid');
                      }
                    }
                  }
                }}
              />
              <TooltipPopover
                contentVariant={isDarkMode ? 'light' : 'dark'}
                triggerContent={
                  <InfoOutlineIcon
                    withHoverEffect
                    fill={vars.color.iconPrimary}
                    size={vars.iconSize.s}
                  />
                }
              >
                <Content id={ContentId.FlightBudgetDurationHelpInfo} />
              </TooltipPopover>
            </Stack>
          </Stack>
          <Stack width="half">
            <PosFormField errors={errors.recurringFlightBudget?.message}>
              <PosCurrencyField
                disabled={disabled}
                errorState={getTextFieldState(errors.recurringFlightBudget)}
                value={recurringFlightBudget?.value ?? ''}
                onChange={(e) => {
                  let newNumber = e.target.value
                    ? Number(e.target.value)
                    : null;

                  if (typeof newNumber !== 'number' || isNaN(newNumber)) {
                    newNumber = null;
                  }

                  setHasManualBudgetChanged(true);
                  setValue(
                    'recurringFlightBudget',
                    posFieldFromValues(
                      newNumber,
                      campaign?.recurringFlightBudget
                    )
                  );
                  clearErrors('recurringFlightBudget');
                }}
                uiCurrency={uiCurrency}
                onBlur={() => {
                  // Validate budget
                  if (
                    !recurringFlightBudget.value ||
                    recurringFlightBudget.value <= 0
                  ) {
                    setError('recurringFlightBudget', {
                      type: 'required',
                      message: requiredText,
                    });
                  }

                  // Validate that the base bid cannot be more than the recurring flight budget
                  if (
                    biddingMode.value === BiddingMode.Manual &&
                    baseBid.value &&
                    // Base bid cannot exceed either the recurring budget or current flight budget (if editing active flight)
                    (baseBid.value > (recurringFlightBudget.value ?? 0) ||
                      (currentFlightBudget.value &&
                        baseBid.value > currentFlightBudget.value))
                  ) {
                    setError('recurringFlightBudget', {
                      type: 'max',
                      message: budgetMustBeGreaterThanBaseBidText as string,
                    });
                  }
                }}
              />
            </PosFormField>
          </Stack>
        </Stack>
      </Stack>

      {biddingMode.value === BiddingMode.Automatic &&
        hasBoostStrategyFeature && (
          <Stack direction="column" gap="m" width="full">
            <Stack gap="m" alignItems="center" className={styles.itemLabel}>
              <span>3.</span>
              <Content id={ContentId.BoostStrategy} />
              <TooltipPopover
                contentVariant={isDarkMode ? 'light' : 'dark'}
                triggerContent={
                  <InfoOutlineIcon
                    withHoverEffect
                    fill={vars.color.iconPrimary}
                    size={vars.iconSize.s}
                  />
                }
              >
                <Content id={ContentId.BoostStrategyDescription} />
              </TooltipPopover>
            </Stack>
            <RadioGroup
              value={spendStrategy?.value ?? SpendStrategy.SteadyHarvest}
              disabled={disabled}
              onValueChange={(v: SpendStrategy) => {
                setValue(
                  'spendStrategy',
                  posFieldFromValues(v, campaign?.spendStrategy ?? null)
                );
              }}
            >
              <Radio
                disabled={disabled}
                value={SpendStrategy.HoldForBetterPrices}
                variant="outline"
                label={
                  <Content id={ContentId.BoostStrategyHoldForBetterPrices} />
                }
                labelSubtext={
                  <Content
                    id={ContentId.BoostStrategyHoldForBetterPricesDescription}
                  />
                }
                rightContent={
                  <BoostStrategyIcon level={2} size={vars.iconSize.m} />
                }
              />
              <Radio
                disabled={disabled}
                value={SpendStrategy.SteadyHarvest}
                variant="outline"
                label={<Content id={ContentId.BoostStrategySteadyHarvest} />}
                labelSubtext={
                  <Content
                    id={ContentId.BoostStrategySteadyHarvestDescription}
                  />
                }
                rightContent={
                  <Stack direction="row" gap="m" alignItems="center">
                    <span className={styles.itemMetadata}>
                      <Content id={ContentId.Recommended} />
                    </span>
                    <BoostStrategyIcon level={3} size={vars.iconSize.m} />
                  </Stack>
                }
              />
              <Radio
                disabled={disabled}
                value={SpendStrategy.MeasuredPush}
                variant="outline"
                label={<Content id={ContentId.BoostStrategyMeasuredPush} />}
                labelSubtext={
                  <Content
                    id={ContentId.BoostStrategyMeasuredPushDescription}
                  />
                }
                rightContent={
                  <BoostStrategyIcon level={4} size={vars.iconSize.m} />
                }
              />
              <Radio
                disabled={disabled}
                value={SpendStrategy.Accelerate}
                variant="outline"
                label={<Content id={ContentId.BoostStrategyAccelerate} />}
                labelSubtext={
                  <Content id={ContentId.BoostStrategyAccelerateDescription} />
                }
                rightContent={
                  <BoostStrategyIcon level={5} size={vars.iconSize.m} />
                }
              />
            </RadioGroup>
          </Stack>
        )}

      <Stack direction="column" gap="m" width="full">
        <Stack alignItems="center" className={styles.itemLabel}>
          <span className={styles.sectionStepNumber}>4.</span>
          <Content id={ContentId.Target} />
          <TooltipPopover
            className={styles.sectionTooltip}
            contentVariant={isDarkMode ? 'light' : 'dark'}
            triggerContent={
              <InfoOutlineIcon
                withHoverEffect
                fill={vars.color.iconPrimary}
                size={vars.iconSize.s}
              />
            }
          >
            <Content id={ContentId.CampaignTargetHelpInfo} />
          </TooltipPopover>
        </Stack>
        {hasFirst3RowsOnlyFeature && (
          <div
            className={styles.subsectionContainer}
            style={{ marginBottom: vars.spacing.m }}
          >
            <Checkbox
              disabled={disabled}
              checked={firstThreeRowsOnly?.value ?? false}
              labelPosition="right"
              label={<Content id={ContentId.FirstThreeRowsOnly} />}
              onChange={() => {
                setValue(
                  'firstThreeRowsOnly',
                  posChangedField(!firstThreeRowsOnly?.value)
                );
              }}
            />
          </div>
        )}
        <RadioGroup
          disabled={disabled}
          value={
            targetTicketClassIds?.value !== null &&
            targetTicketClassIds?.value !== undefined
              ? 'zone'
              : 'default'
          }
          onValueChange={handleTargetChange}
        >
          <Radio
            disabled={disabled}
            value="default"
            variant="outline"
            label={<Content id={ContentId.AllZones} />}
            rightContent={
              <span className={styles.itemMetadata}>
                <Content id={ContentId.Recommended} />
              </span>
            }
          />
          {hasSpecificZonesFeature && (
            <Radio
              disabled={disabled || ticketClasses.length === 0}
              value="zone"
              variant="outline"
              label={
                <>
                  <Content id={ContentId.SpecificZones} />
                  {(targetTicketClassIds?.value?.length ?? 0) > 0 && (
                    <span>
                      {' '}
                      ({targetTicketClassIds!.value!.length} selected)
                    </span>
                  )}
                </>
              }
              rightContent={
                targetTicketClassIds?.value !== null &&
                targetTicketClassIds?.value !== undefined &&
                onToggleMapVisible ? (
                  <ButtonWithIcon
                    icon={<EditIcon withHoverEffect />}
                    iconOnlyMode
                    titleContentId={ContentId.EditSelection}
                    variant="outline"
                    shape="rect"
                    disabled={
                      disabled ||
                      // Disable Modify button when no zones are selected and
                      // the map is already open — the user must pick at least
                      // one zone before they can hide the map panel.
                      (isMapVisible === true &&
                        (targetTicketClassIds?.value?.length ?? 0) === 0)
                    }
                    onClick={(e) => {
                      // Prevent the radio from being re-triggered
                      e.stopPropagation();
                      onToggleMapVisible((prev) => !prev);
                    }}
                  />
                ) : undefined
              }
            />
          )}
          {isMapVisible === false &&
            targetTicketClassIds?.value !== null &&
            targetTicketClassIds?.value !== undefined &&
            targetTicketClassIds?.value?.length > 0 && (
              <ZoneTargetingMapEmbedded />
            )}
        </RadioGroup>
        {errors.targetTicketClassIds?.message && (
          <span
            style={{
              color: vars.color.textError,
              fontSize: vars.typography.fontSize.sm,
            }}
          >
            {errors.targetTicketClassIds.message}
          </span>
        )}
        {!hideZoneList &&
          targetTicketClassIds?.value !== null &&
          targetTicketClassIds?.value !== undefined &&
          ticketClasses.length > 0 && (
            <Stack
              direction="column"
              gap="m"
              width="full"
              className={styles.ticketClassCheckboxContainer}
            >
              {ticketClasses.map((tc) => {
                const isChecked =
                  targetTicketClassIds?.value?.includes(tc.id) ?? false;
                return (
                  <Checkbox
                    key={tc.id}
                    disabled={disabled}
                    checked={isChecked}
                    labelPosition="right"
                    label={
                      <Stack gap="m" alignItems="center">
                        <span
                          className={styles.ticketClassColorDot}
                          style={{ backgroundColor: tc.color }}
                        />
                        <span>{tc.name}</span>
                      </Stack>
                    }
                    onChange={() => {
                      const currentIds = targetTicketClassIds?.value ?? [];
                      const newIds = isChecked
                        ? currentIds.filter((id) => id !== tc.id)
                        : [...currentIds, tc.id];
                      setValue(
                        'targetTicketClassIds',
                        posChangedField(newIds.length > 0 ? newIds : [])
                      );
                    }}
                  />
                );
              })}
            </Stack>
          )}
      </Stack>
      {campaign && <CampaignModalFooter />}
    </Stack>
  );
};
